/**
 * The password an email-only sign-up was created with, held until the person sets their own.
 *
 * Sign-up asks for an email only (28 Oct 2026): the account is created with a generated
 * password (the same way Google One Tap sign-ups are), the person pays, and the welcome step
 * offers "set a password for next time". Amplify's updatePassword needs the old password, so
 * the generated one lives in sessionStorage for this tab until then. If the tab is closed
 * first, nothing is lost: "Forgot password" on the login page sets a password by email, and
 * the flag below lets the app remind them.
 *
 * Why sessionStorage and not a module variable: between sign-up and the pay step the app can
 * do a full reload (an existing session, the Cognito redirect), and a module variable would
 * not survive it. The exposure is the same as the Cognito tokens the same origin already keeps
 * in storage, and the lifetime is kept short: cleared when the password is set, when the
 * person leaves the welcome step, on sign-out, and whenever a different account is signed in.
 */

const PASSWORD_KEY = "ortex_pending_password";
const EMAIL_KEY = "ortex_pending_password_for";
const FLAG_KEY = "ortex_password_pending";

const safe = <T>(fn: () => T, fallback: T): T => {
    try {
        return fn();
    } catch {
        return fallback;
    }
};

export const savePendingPassword = (password: string, email: string): void => {
    safe(() => sessionStorage.setItem(PASSWORD_KEY, password), undefined);
    safe(() => sessionStorage.setItem(EMAIL_KEY, email.trim().toLowerCase()), undefined);
    safe(() => localStorage.setItem(FLAG_KEY, "1"), undefined);
};

/**
 * The generated password for this tab, or null when none is pending, the tab is new, or the
 * signed-in account (by email) is not the one that signed up here; in that last case the stale
 * value is dropped.
 */
export const readPendingPassword = (signedInEmail?: string | null): string | null => {
    const password = safe(() => sessionStorage.getItem(PASSWORD_KEY), null);
    if (!password) return null;
    const owner = safe(() => sessionStorage.getItem(EMAIL_KEY), null);
    if (signedInEmail && owner && owner !== signedInEmail.trim().toLowerCase()) {
        clearPendingPassword();
        return null;
    }
    return password;
};

/** True while the account still runs on its generated password (survives the tab). */
export const isPasswordPending = (): boolean => safe(() => localStorage.getItem(FLAG_KEY) === "1", false);

export const clearPendingPassword = (): void => {
    safe(() => sessionStorage.removeItem(PASSWORD_KEY), undefined);
    safe(() => sessionStorage.removeItem(EMAIL_KEY), undefined);
    safe(() => localStorage.removeItem(FLAG_KEY), undefined);
};
